import React from 'react';
import { Button, PanelArea, Title } from '../../styles';
import { PanelHeader, SubTitle } from '../commons/styles';
import { QuestionBlockSummary } from '../../types';
import {
  getBlockCtaLabel,
  getDisplayedAnsweredQuestions,
  getDisplayedBlockStatus,
} from '../../utils';
import {
  CardsGrid,
  BlockCard,
  BlockCta,
  BlockTitleRow,
  BlockTitle,
  StatusBadge,
  QuestionsCount,
  ProgressBarTrack,
  ProgressBarFill,
  WholeLectureRow,
  WholeLectureHint,
  EmptyState,
} from './styles';

interface QuestionBlocksListProps {
  blocks: QuestionBlockSummary[];
  loadingBlockId: number | null;
  onSelectBlock: (block: QuestionBlockSummary) => void;
  onSelectWholeLecture?: () => void;
  loadingWholeLecture?: boolean;
}

const STATUS_LABEL: Record<QuestionBlockSummary['status'], string> = {
  not_started: 'Não iniciado',
  in_progress: 'Em andamento',
  completed: 'Concluído',
};

export const QuestionBlocksList = ({
  blocks,
  loadingBlockId,
  onSelectBlock,
  onSelectWholeLecture,
  loadingWholeLecture = false,
}: QuestionBlocksListProps) => {
  const anyActionLoading = loadingBlockId !== null || loadingWholeLecture;

  return (
    <PanelArea data-testid="question-blocks-panel-area">
      <PanelHeader>
        <Title width="fit-content">Blocos de questões</Title>
        <SubTitle>Escolha um bloco para iniciar ou continuar</SubTitle>
      </PanelHeader>

      {blocks.length === 0 ? (
        <EmptyState>Nenhum bloco de questões encontrado.</EmptyState>
      ) : (
        <CardsGrid>
          {blocks.map((block) => {
            const displayedStatus = getDisplayedBlockStatus(block);
            const displayedAnswered = getDisplayedAnsweredQuestions(block);
            const percent =
              block.total_questions > 0
                ? Math.min(100, Math.round((displayedAnswered / block.total_questions) * 100))
                : 0;
            const isLoading = loadingBlockId === block.id;
            const isCompleted = displayedStatus === 'completed';

            return (
              <BlockCard key={block.id} $completed={isCompleted}>
                <BlockTitleRow>
                  <BlockTitle>{block.title}</BlockTitle>
                  <StatusBadge $status={displayedStatus}>
                    {STATUS_LABEL[displayedStatus]}
                  </StatusBadge>
                </BlockTitleRow>

                <QuestionsCount>
                  {displayedAnswered}/{block.total_questions} questões
                </QuestionsCount>

                <ProgressBarTrack
                  role="progressbar"
                  aria-valuenow={displayedAnswered}
                  aria-valuemin={0}
                  aria-valuemax={block.total_questions}
                >
                  <ProgressBarFill $percent={percent} />
                </ProgressBarTrack>

                <BlockCta
                  type="button"
                  $completed={isCompleted}
                  disabled={anyActionLoading}
                  onClick={() => onSelectBlock(block)}
                >
                  {isLoading ? 'Carregando...' : getBlockCtaLabel(displayedStatus)}
                </BlockCta>
              </BlockCard>
            );
          })}
        </CardsGrid>
      )}

      {onSelectWholeLecture && (
        <WholeLectureRow>
          <WholeLectureHint>Prefere responder tudo de uma vez?</WholeLectureHint>
          <Button
            type="button"
            variant="invisible"
            disabled={anyActionLoading}
            onClick={onSelectWholeLecture}
          >
            {loadingWholeLecture ? 'Carregando...' : 'Responder a prova inteira'}
          </Button>
        </WholeLectureRow>
      )}
    </PanelArea>
  );
};
